'use client';

import React from 'react';

interface ServiceCardProps {
  number: string;
  category: string;
  title: string;
  description: string;
  features: string[];
  ctaText: string;
  imageSrc: string;
  iconColor: string;
  iconBg: string;
  iconBorder: string;
  icon: React.ReactNode;
  delay: string;
}

function ServiceCard({ number, category, title, description, features, ctaText, imageSrc, iconColor, iconBg, iconBorder, icon, delay }: ServiceCardProps) {
  return (
    <div className={`reveal ${delay} service-card glass-card rounded-3xl overflow-hidden flex flex-col`}>
      {/* Image */}
      <div className="relative h-44 overflow-hidden">
        <img
          alt={title}
          className="w-full h-full object-cover"
          src={imageSrc}
          style={{ opacity: 0.75 }} />
        
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(transparent 30%, rgba(5, 5, 5, 0.95))' }} />
        
        <div className="absolute bottom-4 left-5">
          <div
            className="icon-glow w-11 h-11 rounded-xl flex items-center justify-center"
            style={{ background: iconBg, border: `1px solid ${iconBorder}`, color: iconColor }}>
            
            {icon}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-7 flex flex-col flex-1">
        <span className="text-xs font-semibold tracking-[0.15em] uppercase text-neutral-500 mb-2">{number}</span>
        <h3 className="text-xl font-bold text-white mb-3">{title}</h3>
        <p className="text-sm text-neutral-400 leading-relaxed mb-5">{description}</p>
        <ul className="space-y-2 mb-6 flex-1">
          {features.map((f, i) =>
          <li key={i} className="flex items-center gap-2.5 text-sm text-neutral-400">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
              {f}
            </li>
          )}
        </ul>
        <a
          href="https://calendly.com/creatormaska/30min"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm font-semibold text-blue-400 hover:text-blue-300 transition-colors group">
          
          {ctaText}
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="group-hover:translate-x-1 transition-transform" aria-hidden="true">
            <path d="M5 12h14" />
            <path d="m12 5 7 7-7 7" />
          </svg>
        </a>
      </div>
    </div>);

}

export default function ServicesSection() {
  const services = [
  {
    number: '01 — Content',
    category: 'content',
    title: 'AI Content Systems',
    description: 'Viral content that converts. We build AI-powered content engines that produce, schedule, and distribute high-performing posts across every platform — consistently.',
    features: ['AI-generated scripts & hooks', 'Cross-platform distribution', 'Viral trend optimization'],
    ctaText: 'Start Creating',
    imageSrc: 'https://static.prod-images.emergentagent.com/jobs/be03ca89-6fb3-45c2-9468-7b5426f65782/images/29dc920c17f7e6304f73bcd5a8398b971833620231abbbef8791a095e15529b8.png',
    iconColor: 'rgb(59, 130, 246)',
    iconBg: 'rgba(59, 130, 246, 0.15)',
    iconBorder: 'rgba(59, 130, 246, 0.3)',
    icon:
    <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" />
        </svg>,

    delay: 'reveal-delay-1'
  },
  {
    number: '02 — Automation',
    category: 'automation',
    title: 'Automation & Workflows',
    description: 'Reclaim 40+ hours every week. We automate your repetitive tasks, client onboarding, and business operations so you can focus on growth — not grind.',
    features: ['CRM & email automation', 'AI-powered lead nurturing', 'Custom workflow builds'],
    ctaText: 'Automate Now',
    imageSrc: 'https://static.prod-images.emergentagent.com/jobs/be03ca89-6fb3-45c2-9468-7b5426f65782/images/8a045e380b6987e70025cdabdef1692a15d5f2264a1ceead3aa7ce78bce99f6f.png',
    iconColor: 'rgb(157, 76, 221)',
    iconBg: 'rgba(157, 76, 221, 0.15)',
    iconBorder: 'rgba(157, 76, 221, 0.3)',
    icon:
    <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 8V4H8" />
          <rect width="16" height="12" x="4" y="8" rx="2" />
          <path d="M2 14h2" />
          <path d="M20 14h2" />
          <path d="M15 13v2" />
          <path d="M9 13v2" />
        </svg>,

    delay: 'reveal-delay-2'
  },
  {
    number: '03 — Growth',
    category: 'growth',
    title: 'Growth & Scaling',
    description: 'Turn traffic into revenue. Our growth systems optimize your funnel, maximize conversions, and build predictable income streams from your existing audience.',
    features: ['Revenue funnel architecture', 'Audience monetization', 'Performance analytics'],
    ctaText: 'Start Scaling',
    imageSrc: 'https://static.prod-images.emergentagent.com/jobs/be03ca89-6fb3-45c2-9468-7b5426f65782/images/3582c9faf568709eb023c68c2385e3d9d38dca602234a5c2c2a0f4d36915567d.png',
    iconColor: 'rgb(59, 130, 246)',
    iconBg: 'rgba(59, 130, 246, 0.15)',
    iconBorder: 'rgba(59, 130, 246, 0.3)',
    icon:
    <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
          <polyline points="16 7 22 7 22 13" />
        </svg>,

    delay: 'reveal-delay-3'
  }];


  return (
    <section
      id="services"
      className="relative py-24 sm:py-32 overflow-hidden"
      style={{ background: 'rgb(5, 5, 5)' }}>
      
      {/* Background orb */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          style={{ position: 'absolute', top: '20%', left: '50%', transform: 'translateX(-50%)', width: 700, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(59, 130, 246, 0.05) 0%, transparent 70%)', filter: 'blur(40px)' }} />
        
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header */}
        <div className="reveal text-center mb-16">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-400 mb-4 block">What We Do</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-4" style={{ letterSpacing: '-0.02em' }}>Services Built to Scale</h2>
          <p className="text-base text-neutral-500 max-w-xl mx-auto">Three core offerings designed to grow your brand, save your time, and multiply your revenue.</p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
          {services.map((s) =>
          <ServiceCard key={s.category} {...s} />
          )}
        </div>
      </div>
    </section>);

}